import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import type { MediaItem, Playlist } from "@gooner/shared";
import { clipKey } from "@gooner/shared";
import { adminApi } from "../lib/api";
import { Button, TextInput, cx } from "../control/ui/primitives";
import { IconPlus } from "../control/ui/icons";
import { ClipStats } from "../control/components/ClipStats";
import { ClipViewer } from "../control/components/ClipViewer";
import { TagBubble, TagBubbleRow } from "../control/components/TagBubble";

/**
 * Admin single-clip editor: add freeform tags (with autocomplete), toggle the clip
 * across collections, and ban/unban it. Built on the shared ClipViewer shell (the
 * same one the public preview uses) — this file only supplies the admin controls.
 * Collection toggles are delegated to the parent (which owns that state).
 */
export function ClipLightbox({
  clip,
  collections,
  selected,
  onToggleCollection,
  onCreateCollection,
  onPrev,
  onNext,
  onBanChanged,
  onClose,
}: {
  clip: MediaItem;
  collections: Playlist[];
  selected: Set<string>;
  onToggleCollection: (id: string, isIn: boolean) => Promise<void>;
  onCreateCollection: (name: string) => Promise<void>;
  /** Navigate to the previous/next clip (arrow keys + ‹ › buttons). Omit at ends. */
  onPrev?: () => void;
  onNext?: () => void;
  /** Called after the clip's banned state changes (so the parent can refresh). */
  onBanChanged?: (banned: boolean) => void;
  onClose: () => void;
}) {
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [tagFocus, setTagFocus] = useState(false);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [newCol, setNewCol] = useState("");
  const [banned, setBanned] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  // Load persisted tags + banned state for this clip. Reset optimistically first so a
  // navigated-to clip never briefly shows the previous clip's data.
  useEffect(() => {
    setTags([]);
    setBanned(false);
    let cancelled = false;
    adminApi
      .getClipMeta(clipKey(clip))
      .then((m) => {
        if (cancelled) return;
        setTags(m.tags);
        setBanned(m.banned);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [clip]);

  // Load the tag palette once (for autocomplete). Not per-clip.
  useEffect(() => {
    adminApi
      .listTags()
      .then((r) => setAllTags(r.tags))
      .catch(() => undefined);
  }, []);

  function saveTags(t: string[]) {
    setTags(t);
    void adminApi.setClipMeta(clip, { tags: t }).catch(() => undefined);
  }
  function saveBan(next: boolean) {
    setBanned(next);
    void adminApi
      .setClipMeta(clip, { banned: next })
      .then(() => onBanChanged?.(next))
      .catch(() => undefined);
  }

  function addTag(value?: string) {
    const t = (value ?? tagInput).trim().slice(0, 40);
    if (!t || tags.some((x) => x.toLowerCase() === t.toLowerCase())) {
      setTagInput("");
      return;
    }
    saveTags([...tags, t]);
    setTagInput("");
  }

  // Existing tags matching what's typed (excluding ones already on this clip).
  const tagQuery = tagInput.trim().toLowerCase();
  const tagSuggestions = allTags
    .filter((t) => !tags.some((x) => x.toLowerCase() === t.toLowerCase()))
    .filter((t) => (tagQuery ? t.toLowerCase().includes(tagQuery) : true))
    .slice(0, 8);

  async function createCol(e: FormEvent) {
    e.preventDefault();
    const name = newCol.trim();
    if (!name) return;
    setBusy("new");
    try {
      await onCreateCollection(name);
    } finally {
      setBusy(null);
      setNewCol("");
    }
  }

  return (
    <ClipViewer
      clip={clip}
      title="Tags & collections"
      onPrev={onPrev}
      onNext={onNext}
      onClose={onClose}
      mediaOverlay={
        banned ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-red-950/60">
            <span className="rounded-md border border-red-400/70 bg-red-950/80 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-red-200">
              Banned
            </span>
          </div>
        ) : undefined
      }
      footer={
        <form onSubmit={createCol} className="flex gap-2 border-t border-[var(--color-edge)] p-3">
          <TextInput
            value={newCol}
            onChange={(e) => setNewCol(e.target.value)}
            placeholder="New collection…"
            maxLength={80}
          />
          <Button
            type="submit"
            variant="primary"
            disabled={!newCol.trim() || busy === "new"}
            className="shrink-0"
          >
            <IconPlus />
            {busy === "new" ? "…" : "Add"}
          </Button>
        </form>
      }
    >
      {/* ban toggle */}
      <div className="flex justify-end">
        {banned ? (
          <Button size="sm" variant="subtle" onClick={() => saveBan(false)}>
            Unban clip
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => saveBan(true)}
            className="text-red-400 hover:text-red-300"
          >
            Ban clip
          </Button>
        )}
      </div>

      {/* freeform tags */}
      <div>
        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-400">Tags</div>
        {tags.length > 0 && (
          <TagBubbleRow className="mb-2">
            {tags.map((t) => (
              <TagBubble
                key={t}
                tag={t}
                onRemove={(tag) => saveTags(tags.filter((x) => x !== tag))}
              />
            ))}
          </TagBubbleRow>
        )}
        <div className="relative">
          <TextInput
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onFocus={() => setTagFocus(true)}
            onBlur={() => setTimeout(() => setTagFocus(false), 120)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addTag();
              }
            }}
            placeholder="Add a tag + Enter…"
            maxLength={40}
          />
          {tagFocus && tagSuggestions.length > 0 ? (
            <div className="absolute z-10 mt-1 max-h-44 w-full overflow-auto rounded-md border border-[var(--color-edge)] bg-[var(--color-panel-2)] py-1 shadow-xl">
              {tagSuggestions.map((t) => (
                <button
                  key={t}
                  type="button"
                  // mousedown (not click) so it fires before the input blur closes this.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addTag(t);
                  }}
                  className="block w-full px-3 py-1 text-left text-xs text-zinc-200 hover:bg-[var(--color-accent)]/20"
                >
                  {t}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {/* collections checklist */}
      <div>
        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-400">
          Collections
        </div>
        {collections.length === 0 ? (
          <p className="text-xs text-zinc-500">None yet — create one below.</p>
        ) : (
          <ul className="space-y-1">
            {collections.map((c) => {
              const isIn = selected.has(c.id);
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    disabled={busy === c.id}
                    onClick={async () => {
                      setBusy(c.id);
                      try {
                        await onToggleCollection(c.id, isIn);
                      } finally {
                        setBusy(null);
                      }
                    }}
                    className={cx(
                      "flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                      isIn
                        ? "border-[var(--color-accent)] bg-[var(--color-accent)]/10 text-zinc-100"
                        : "border-[var(--color-edge)] bg-[var(--color-ink)] text-zinc-300 hover:border-[var(--color-accent)]/50",
                    )}
                  >
                    <span
                      className={cx(
                        "flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px]",
                        isIn
                          ? "border-[var(--color-accent)] bg-[var(--color-accent)] text-white"
                          : "border-[var(--color-edge)]",
                      )}
                    >
                      {isIn ? "✓" : ""}
                    </span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="shrink-0 text-xs text-zinc-500">{c.clipCount}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* stats */}
      <div>
        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-400">Stats</div>
        <ClipStats clip={clip} linkable={false} />
      </div>
    </ClipViewer>
  );
}
